import React, { memo, useState } from "react";
import { Input } from "@components/atoms";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useValueFromPayload } from "@redux/slices/searchFilters";
import { VDBIcons } from "@utils/VDBIcons";
import useFilterSearchScreenModal from "@scenes/explore/useFilterSearchScreenModal";
import { useTheme } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { useGetAllBrandsList } from "@apis/useBrandsList";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { useSelector } from "react-redux";

type SearchFilterSectionProps = {
  data: any;
  payloadName?: Array<string>;
  selectedStyle: SelectedItemTheme;
  sectionName?: string;
  children?: React.ReactNode;
  sub_filters?: Array<any>;
};

const SearchFilterSection = ({
  data = [],
  sectionName = "",
  selectedStyle,
  children,
  sub_filters = [],
}: SearchFilterSectionProps) => {
  const value = useValueFromPayload(sectionName, data?.payload_name);
  const selectedItems = value?.join(", ");
  const theme = useTheme();
  const { renderModal, showModal } = useFilterSearchScreenModal();
  const [hovered, setHovered] = useState<boolean | null>(false);
  const { t } = useTranslation();
  const checkBoxValue = useValueFromPayload(
    sectionName,
    sub_filters?.[0]?.options?.[0]?.payload_name
  );

  const { selectedGroups } = useSearchGroupSlice();
  const productType = useSelector(
    (state: any) => state.appState?.activeProduct
  );

  let mGroupIds = selectedGroups[productType] ?? [];
  let vdbSetting = false;
  if (mGroupIds.includes(0)) {
    vdbSetting = true;
    mGroupIds = mGroupIds.filter((groupId: number) => groupId !== 0);
  }

  const isEnableBrandSearch = data?.payload_name?.includes("brands");

  const { data: brandsData, isLoading: brandsLoading } = useGetAllBrandsList(
    mGroupIds,
    vdbSetting,
    {
      enabled: isEnableBrandSearch,
    }
  );

  const openFilterSearchScreen = () => {
    const props = {
      selectionType: data?.selection_type,
      title: t(data?.label),
      customIcon: data?.icon_name,
      payloadName: data?.payload_name,
      value: value,
      sectionName: sectionName,
      api: data?.api || "",
      server_side_search: data?.server_side_search,
      selectedStyle: selectedStyle,
      api_query_key: data?.api_query_key,
      name: data?.name,
      inputLabel: data?.input_label,
      sub_options: sub_filters,
      brandsData: brandsData,
      brandsLoading: brandsLoading,
      defaultList: data?.default_list ?? [],
    };
    if (!theme.isLargeScreen) {
      // @ts-ignore
      navigate(Routes.FilterSearchScreen, props);
    } else {
      showModal(props);
    }
  };

  return (
    <>
      <TouchableContainer
        onPress={openFilterSearchScreen}
        isHovered={hovered}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        selectedStyle={selectedStyle}
        reducePadding={sub_filters?.length > 0}
      >
        <Input
          leftIcon={data?.icon_name ? data?.icon_name : ""}
          rightIcon={VDBIcons.MAIN_SEARCH}
          inputLabel={t(data?.label) || ""}
          pointerEvents="none"
          value={selectedItems || ""}
          disabled={true}
          ignoreDisabledStyle={true}
          selectedStyle={selectedStyle}
          isHideBorder={true}
        />
        {renderModal()}
      </TouchableContainer>
      {children && (
        <React.Fragment key={JSON.stringify(checkBoxValue ?? "initial")}>
          {children}
        </React.Fragment>
      )}
    </>
  );
};

const TouchableContainer = styled.Pressable<{
  isHovered: boolean;
  selectedStyle?: SelectedItemTheme;
  reducePadding?: boolean;
}>`
  margin-left: ${SpaceType.Double}px;
  margin-right: ${SpaceType.Double}px;
  margin-top: 15px;
  margin-bottom: ${({ reducePadding }) => (reducePadding ? "13px" : "3px")};
  border-color: ${({
    theme,
    isHovered,
    selectedStyle,
  }: {
    theme: DefaultTheme;
    isHovered: boolean;
    selectedStyle: SelectedItemTheme;
  }) =>
    isHovered
      ? (theme[
          selectedStyle
            ? (selectedStyle?.secondary as keyof DefaultTheme)
            : "main-04"
        ] as string)
      : theme["core-04"]};
  border-width: 1.5px;
  border-radius: ${SpaceType.Basic}px;
`;

export default memo(SearchFilterSection);
